<template>
  <Modal name="CREATE_UPDATE_SERVICE_PROVIDER_USER" :title="titleLabel" ref="modal">
    <template #body>
      <h-loading v-if="isServiceProviderLoading" />
      <v-form
        v-show="!isServiceProviderLoading"
        v-model="isValid"
        autocomplete="off"
        ref="form"
        lazy-validation
      >
        <HTextInput
          v-model="name"
          :label="nameLabel"
          :rules="[rules.name]"
          :loading="isServiceProviderLoading"
          inside-label
        />
        <HTextInput
          v-model="email"
          :label="emailLabel"
          :rules="[rules.email]"
          autocomplete="off"
          :loading="isServiceProviderLoading"
          inside-label
        />
        <HDocumentID
          v-model="cpf"
          :default-value="cpf"
          :country="country"
          :label="cpfLabel"
          :rules="[rules.documentCheck]"
        />
        <HPhone
          v-model="phone"
          :default-value="phone"
          :label="phoneLabel"
          :rules="[rules.phone]"
          :loading="isServiceProviderLoading"
          inside-label
        />
        <HCitySelect
          v-model="city_id"
          :label="cityLabel"
          :rules="[rules.required]"
          :country="country"
          :disabled="isServiceProviderLoading"
          :defaultValue="city"
        />
        <span v-if="!id">
          <hr class="v-divider theme--light mb-6" />
          <v-row>
            <v-col>
              <HTextInput
                v-model="password"
                :label="passwordLabel"
                :rules="[rules.password]"
                :loading="isServiceProviderLoading"
                autocomplete="off"
                type="password"
                inside-label
              />
            </v-col>
            <v-col>
              <HTextInput
                v-model="passwordMatch"
                :label="repeatPasswordLabel"
                :rules="[rules.match(password, passwordMatch)]"
                :loading="isServiceProviderLoading"
                autocomplete="off"
                type="password"
                inside-label
              />
            </v-col>
          </v-row>
        </span>
        <div class="d-flex justify-end gap-1">
          <v-btn
            @click="closeModal"
            :disabled="isServiceProviderLoading"
            elevation="0"
            text
          >
            {{ cancelLabel }}
          </v-btn>
          <v-btn
            @click="createUser"
            :loading="isServiceProviderLoading"
            color="primary"
            elevation="0"
          >
            {{ actionLabel }}
          </v-btn>
        </div>
      </v-form>
    </template>
  </Modal>
</template>

<script>
// vue core
import { mapGetters, mapActions } from 'vuex'

// utils
import {
  name,
  email,
  phone,
  match,
  password,
  documentCheck
} from '@/util/errorHandling'

// components
import Modal from '../../../components/app/Modal.vue'
import HPhone from '../../../components/app/HPhone.vue'
import HTextInput from '../../../components/app/HTextInput.vue'
import HDocumentID from '../../../components/app/HDocumentID.vue'
import HCitySelect from '../../../components/app/HCitySelect.vue'

export default {
  name: 'CreateServiceProviderUser',

  components: {
    Modal,
    HPhone,
    HTextInput,
    HCitySelect,
    HDocumentID,
  },

  computed: {
    ...mapGetters(['isServiceProviderLoading', 'serviceProviderUser']),

    country() {
      return localStorage.getItem('user_country')
    },

    actionLabel() {
      return this.id ? this.updateLabel : this.createLabel
    },

    titleLabel() {
      return this.id ? this.updateLabel : this.createLabel
    },
  },

  methods: {
    ...mapActions(['closeModal', 'createServiceProviderUser', 'updateServiceProviderUser']),

    resetFormdata() {
      this.id = null
      this.name = null
      this.cpf = null
      this.email = null
      this.phone = null
      this.password = null
      this.passwordMatch = null
      this.city_id = null
      this.city = null

      this.$refs.form?.resetValidation()
    },

    createUser() {
      if (!this.$refs.form.validate()) return

      if (!this.id) {
        this.createServiceProviderUser({
          name: this.name,
          cpf: this.cpf,
          email: this.email,
          phone: this.phone,
          password: this.password,
          city_id: this.city_id,
        })
      } else {
        this.updateServiceProviderUser({
          id: this.id,
          name: this.name,
          cpf: this.cpf,
          email: this.email,
          phone: this.phone,
          city_id: this.city_id,
        })
      }
    },
  },

  watch: {
    serviceProviderUser(serviceProviderUser) {
      if (serviceProviderUser) {
        this.id = serviceProviderUser.id
        this.name = serviceProviderUser.name
        this.cpf = serviceProviderUser.cpf
        this.email = serviceProviderUser.email
        this.phone = serviceProviderUser.phone
        this.city_id = serviceProviderUser.city?.id
        this.city = serviceProviderUser.city

        this.$refs.form.resetValidation()
      }
    },
  },

  data() {
    return {
      // user data
      id: null,
      name: '',
      cpf: '',
      email: '',
      phone: '',
      password: '',
      passwordMatch: '',
      city_id: null,
      city: null,

      // form stuff
      isValid: false,
      isLoading: false,

      // labels
      nameLabel: this.$t('hausefulUsers.label.name'),
      cpfLabel: this.$t('hausefulUsers.label.document'),
      emailLabel: this.$t('hausefulUsers.label.email'),
      phoneLabel: this.$t('hausefulUsers.label.phone'),
      cityLabel: this.$t('hausefulUsers.label.city'),
      passwordLabel: this.$t('hausefulUsers.label.password'),
      repeatPasswordLabel: this.$t('hausefulUsers.label.repeatPassword'),
      cancelLabel: this.$t('hausefulUsers.action.cancel'),
      createLabel: this.$t('hausefulUsers.action.create'),
      updateLabel: this.$t('hausefulUsers.action.edit'),

      // rules
      rules: {
        name,
        email,
        phone,
        match,
        password,
        documentCheck,
      }
    }
  },

  mounted() {
    const modal = this.$refs.modal
    this.$watch(() => modal.dialog, (value) => {
      if (value) this.resetFormdata()
    })
  }
}
</script>
